import React, { useState, useEffect } from 'react';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { Radio } from '../Radio/Radio';
import { Button } from '../Button/Button';
import { Switch } from '../Switch/Switch';
import { FormAlert } from '../FormAlert';
import { Project } from '../Dashboard';
import { ProjectMembersApiService, ProjectMemberResponse } from '../../services/projectMembersApi';
import { getNameFromEmail, getInitials, formatProjectDate } from '../../utils/projectUtils';
import { ProjectRole, BackendProjectRole, mapBackendRoleToUiRole, PlatformType } from '../../types/project';
import { getPlatformBadgeClass } from '../../config/platformConfig';
import { MoreActions } from '../MoreActions/MoreActions';
import { IMoreActionItem } from '../MoreActions/MoreActions.types';
import { isValidEmail } from '../../admin/utils';
import { useSlidingPill } from '../../hooks/useSlidingPill';

interface ManageCollaborationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProject?: Project;
  onProjectUpdate?: (updatedProject: Project) => void;
}

interface Collaborator {
  id: string;
  name: string;
  email: string;
  initials: string;
  addedDate: string;
  lastActive: string;
  role: 'Owner' | 'Editor' | 'Viewer';
}

// Skeleton Loading Component for Collaborator Rows
const CollaboratorRowSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div
      className="flex flex-col items-start px-bg-v2-md py-bg-v2-lg rounded-bg-v2-md w-full animate-fade-in-up opacity-0"
      style={{
        animationDelay: `${index * 0.08}s`,
        animationFillMode: 'forwards'
      }}
    >
      <div className="flex items-start justify-between w-full">
        {/* Left: Avatar and Info Skeleton */}
        <div className="flex gap-bg-v2-lg items-center">
          {/* Avatar Skeleton */}
          <div className="w-[58px] h-[58px] rounded-full skeleton-shimmer shrink-0"></div>

          {/* Info Skeleton */}
          <div className="flex flex-col items-start gap-bg-v2-xs">
            {/* Name Skeleton */}
            <div className="h-5 skeleton-shimmer rounded w-32"></div>
            {/* Email Skeleton */}
            <div className="h-5 skeleton-shimmer rounded w-40"></div>
            {/* Date Skeleton */}
            <div className="h-4 skeleton-shimmer rounded w-24"></div>
          </div>
        </div>

        {/* Right: Role Badge Skeleton */}
        <div className="flex flex-col gap-bg-v2-md items-end justify-center">
          <div className="h-6 skeleton-shimmer rounded-bg-v2-sm w-16"></div>
        </div>
      </div>
    </div>
  );
};

export const ManageCollaborationModal: React.FC<ManageCollaborationModalProps> = ({
  isOpen,
  onClose,
  selectedProject,
  onProjectUpdate,
}) => {
  const projectName = selectedProject?.title || 'Employee Management System';
  const projectType = selectedProject?.project_type === 1
    ? 'Web'
    : selectedProject?.project_type === 2 && selectedProject?.platform_type === PlatformType.ServiceNow
      ? 'SN'
      : selectedProject?.project_type === 2 && selectedProject?.platform_type === PlatformType.PowerApps
      ? 'PA'
      : selectedProject?.project_type === 2 && selectedProject?.platform_type === PlatformType.Salesforce
      ? 'SF'
      : selectedProject?.project_type === 2
        ? 'TAP'
        : 'Web';

  const badgeClass = getPlatformBadgeClass(
    selectedProject?.platform_type as PlatformType | undefined,
    selectedProject?.project_type === 1,
  );
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const { containerRef: tabsRef, registerTab, rect: pillRect, animate: pillAnimate } = useSlidingPill(activeTab);
  const [permissionType, setPermissionType] = useState<'viewer' | 'editor'>('viewer');
  const [paymentType, setPaymentType] = useState<'owner_pay' | 'collaborator_pay'>('owner_pay');

  const [email, setEmail] = useState('');
  const [emailTags, setEmailTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // State for members
  const [members, setMembers] = useState<ProjectMemberResponse[]>([]);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [memberActionError, setMemberActionError] = useState('');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuItems, setMenuItems] = useState<IMoreActionItem[]>([]);

  // Derived state for MoreActions trigger
  const triggerRef = React.useMemo(() => ({ current: anchorEl }), [anchorEl]);

  // The members API rejects with { message: { detail }, status }; the detail is the
  // backend's own wording (e.g. a guest cannot be made an editor), so show it as is.
  const memberActionMessage = (error: any, fallback: string): string => {
    const detail = error?.message?.detail;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    if (!selectedProject?.id) return;
    setMemberActionError('');
    try {
      await ProjectMembersApiService.updateMemberRole(selectedProject.id, memberId, newRole);
      setMembers(prev => prev.map(m =>
        m._id === memberId ? { ...m, role: newRole } : m
      ));
    } catch (error: any) {
      console.error('Failed to update role:', error);
      setMemberActionError(memberActionMessage(error, 'Failed to update the member role. Please try again.'));
    } finally {
      setActiveMenuId(null);
      setAnchorEl(null);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedProject?.id) return;
    if (!confirm('Are you sure you want to remove this member?')) return;
    setMemberActionError('');
    try {
      await ProjectMembersApiService.removeMember(selectedProject.id, memberId);
      setMembers(prev => prev.filter(m => m._id !== memberId));
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      setMemberActionError(memberActionMessage(error, 'Failed to remove the member. Please try again.'));
    } finally {
      setActiveMenuId(null);
      setAnchorEl(null);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, collaborator: Collaborator) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveMenuId(collaborator.id);

    // Build menu items
    const items: IMoreActionItem[] = [
      {
        id: 'toggle-role',
        label: collaborator.role === ProjectRole.VIEWER ? 'Make Editor' : 'Make Viewer',
        onClick: () => handleUpdateRole(collaborator.id, collaborator.role === ProjectRole.VIEWER ? BackendProjectRole.COLLABORATOR : BackendProjectRole.VIEWER),
      },
      {
        id: 'remove',
        label: 'Remove from project',
        variant: 'destructive',
        onClick: () => handleRemoveMember(collaborator.id),
      }
    ];
    setMenuItems(items);
  };

  // Helper to derive display data from member
  const getDisplayData = (member: ProjectMemberResponse): Collaborator => {
    const email = member.invited_user;
    const name = member.invited_user_name || getNameFromEmail(email);
    const initials = getInitials(name);

    // Format date
    const dateValue = member.accepted_at || member.invited_at;
    const addedDate = formatProjectDate(dateValue);

    // Map role
    const role = mapBackendRoleToUiRole(member.role);

    return {
      id: member._id,
      name: name,
      email: email,
      initials: initials,
      addedDate: addedDate,
      lastActive: 'N/A', // Not available in this API
      role: role,
    };
  };

  const fetchMembers = async () => {
    if (!selectedProject?.id) return;

    setIsFetchingMembers(true);
    try {
      // Fetch all members (no filter) so we can compute counts for tabs
      const response = await ProjectMembersApiService.getProjectMembers(selectedProject.id, {
        is_deleted: false
      });
      setMembers(response.members);
    } catch (error) {
      console.error('Failed to fetch project members:', error);
    } finally {
      setIsFetchingMembers(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedProject?.id) {
      fetchMembers();
    }
  }, [isOpen, selectedProject]);

  const activeMembersRaw = members.filter(m => m.invite_status === 'accepted');
  const pendingMembersRaw = members.filter(m => m.invite_status === 'pending');

  const displayMembers = activeTab === 'active'
    ? activeMembersRaw.map(getDisplayData)
    : pendingMembersRaw.map(getDisplayData);

  // Filter by search query
  const filteredMembers = displayMembers.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = activeMembersRaw.length;
  const pendingCount = pendingMembersRaw.length;

  // Reset form when modal opens and load shareable link state
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setEmailTags([]);
      setInviteError('');
      setMemberActionError('');
      setPermissionType('viewer');
      setSearchQuery('');
      setActiveTab('active');
      setLinkCopied(false);
      // Load shareable link setting from project
      setShareableLinkEnabled(selectedProject?.readonly_sharable_link_enabled || false);
    }
  }, [isOpen, selectedProject]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if comma is entered
    if (value.includes(',')) {
      const emails = value.split(',').map(email => email.trim()).filter(email => email);
      if (emails.length > 0) {
        // Add only valid emails as tags
        const validEmails = emails.filter(email => isValidEmail(email) && !emailTags.includes(email));

        if (validEmails.length > 0) {
          setEmailTags([...emailTags, ...validEmails]);
          setEmail('');
        } else {
          // Keep invalid email in input (remove comma)
          setEmail(value.replace(',', ''));
        }
      } else {
        setEmail('');
      }
    } else {
      setEmail(value);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && email.trim()) {
      e.preventDefault();
      const trimmedEmail = email.trim();

      if (!isValidEmail(trimmedEmail)) {
        return;
      }

      if (!emailTags.includes(trimmedEmail)) {
        setEmailTags([...emailTags, trimmedEmail]);
        setEmail('');
      }
    }
    // Handle backspace to remove last tag when input is empty
    if (e.key === 'Backspace' && !email && emailTags.length > 0) {
      setEmailTags(emailTags.slice(0, -1));
    }
  };

  const removeEmailTag = (indexToRemove: number) => {
    setEmailTags(emailTags.filter((_, index) => index !== indexToRemove));
  };

  const handleInvite = async () => {
    const allEmails = [...emailTags];
    if (email.trim()) {
      allEmails.push(email.trim());
    }

    if (allEmails.length === 0 || !selectedProject?.id) return;

    // Validate emails
    const invalidEmails = allEmails.filter(e => !isValidEmail(e));

    if (invalidEmails.length > 0) {
      alert(`Invalid email format: ${invalidEmails.join(', ')}`);
      return;
    }

    setIsInviting(true);
    setInviteError('');
    try {
      // Map UI role to backend role
      const roleToSend = permissionType === 'viewer' ? BackendProjectRole.VIEWER : BackendProjectRole.COLLABORATOR;

      const response = await ProjectMembersApiService.inviteMember(selectedProject.id, allEmails, roleToSend, paymentType);

      // Handle partial failures or specific messages
      if (response && response.message) {
        if (response.failed_emails && response.failed_emails.length > 0) {
          // Format failed emails in a more user-friendly way
          const failedList = response.failed_emails.map((email: string) => `  • ${email}`).join('\n');
          alert(`${response.message}\n\n${failedList}`);
        } else {
          // Success message
          alert(response.message);
        }
      }

      // Reset form
      setEmailTags([]);
      setEmail('');

      // Refresh list
      await fetchMembers();

    } catch (error: any) {
      console.error('Failed to invite members:', error);
      const errorMessage = error?.message?.detail || error?.message || 'Unable to send invites. Please try again.';
      setInviteError(typeof errorMessage === 'string' ? errorMessage : 'Unable to send invites. Please try again.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleShareableLinkToggle = async (enabled: boolean) => {
    if (!selectedProject?.id) return;
    
    // Optimistically update UI
    setShareableLinkEnabled(enabled);
    
    try {
      await ProjectMembersApiService.updateShareableLinkSetting(selectedProject.id, enabled);
      
      // Update parent component's project state
      if (onProjectUpdate && selectedProject) {
        const updatedProject = {
          ...selectedProject,
          readonly_sharable_link_enabled: enabled
        };
        onProjectUpdate(updatedProject);
      }
    } catch (error) {
      console.error('Failed to update shareable link setting:', error);
      // Revert on error
      setShareableLinkEnabled(!enabled);
      alert('Failed to update shareable link setting. Please try again.');
    }
  };

  const handleCopyLink = async () => {
    if (!selectedProject?.id) return;
    
    try {
      // Construct the shareable link URL
      const currentHost = window.location.origin;
      const basePath = selectedProject.project_type === 2 ? '/fedramp' : '';
      const shareableLink = `${currentHost}${basePath}/chat/${selectedProject.id}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shareableLink);
      
      // Show success indication
      setLinkCopied(true);
      
      // Reset the indication after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
      
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link to clipboard. Please try again.');
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-end bg-alpha-black-70 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="manage-collaboration-title"
    >
      {/* Slide-out Panel */}
      <div
        className={`bg-bg-primary h-full flex flex-col w-full max-w-[500px] shadow-bg-v2-2xl border-l border-border-secondary overflow-y-auto ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-bg-primary flex gap-bg-v2-md items-start p-bg-v2-3xl border-b border-border-secondary shrink-0">
          <div className="flex-1 flex flex-col gap-bg-v2-xxs">
            <h2 id="manage-collaboration-title">
              <Text
                variant="text-lg"
                weight="semibold"
                color="primary"
                className="font-bg-v2-sans"
              >
                Manage Collaboration
              </Text>
            </h2>
            <Text
              variant="text-sm"
              weight="regular"
              color="secondary"
              className="font-bg-v2-sans"
            >
              Add or remove collaborators to this project.
            </Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            aria-label="Close modal"
          >
            <Icon name={IconName.XClose} size="md" inheritColor />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-xl items-start px-bg-v2-xl py-0 flex-1">
          {/* Divider */}
          <div className="bg-border-secondary h-px w-full" />

          {/* Project Name Section */}
          <div className="flex flex-col gap-bg-v2-md items-start px-bg-v2-md py-0 w-full">
            <div className="flex items-center w-full">
              <Text
                variant="text-lg"
                weight="semibold"
                color="primary"
                className="flex-1 font-bg-v2-sans"
              >
                {projectName}
              </Text>
              <div className={`${badgeClass} px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full`}>
                <Text
                  variant="text-xs"
                  weight="medium"
                  /* the pill owns the text colour, so don't let Text's
                     default `primary` win over it */
                  className="font-bg-v2-sans !text-inherit"
                >
                  {projectType}
                </Text>
              </div>
            </div>
          </div>

          {/* Invite Collaborators Section */}
          <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-xl p-bg-v2-2xl flex flex-col gap-bg-v2-xl w-full">
            <div className="flex flex-col gap-bg-v2-md w-full">
              <Text
                variant="text-md"
                weight="semibold"
                color="primary"
                className="font-bg-v2-sans"
              >
                Invite Collaborators
              </Text>
              <div className="flex flex-col items-start px-0 py-bg-v2-xs w-full">
                <div className="bg-border-secondary h-px w-full" />
              </div>

              <div className="flex flex-col gap-bg-v2-2xl w-full">
                {/* Email Input */}
                <div className="flex flex-col gap-bg-v2-sm w-full">
                  <Text
                    variant="text-sm"
                    weight="medium"
                    color="secondary"
                    as="label"
                    className="font-bg-v2-sans"
                  >
                    Email
                  </Text>
                  <div className="bg-bg-primary border border-border-primary rounded-bg-v2-md shadow-bg-v2-xs flex gap-bg-v2-sm items-center px-bg-v2-lg py-bg-v2-md w-full flex-wrap min-h-[44px]">
                    <Icon name={IconName.Mail} size="md" variant="secondary" className="shrink-0" />

                    {/* Email Tags */}
                    {emailTags.map((tag, index) => (
                      <div
                        key={index}
                        className="bg-bg-brand-primary-alt border border-border-brand-alt rounded-bg-v2-md px-bg-v2-md py-bg-v2-xs flex gap-bg-v2-xs items-center"
                      >
                        <Text
                          variant="text-sm"
                          weight="medium"
                          color="primary"
                          className="font-bg-v2-sans"
                        >
                          {tag}
                        </Text>
                        <button
                          onClick={() => removeEmailTag(index)}
                          className="hover:opacity-70 transition-opacity flex items-center justify-center"
                          aria-label={`Remove ${tag}`}
                        >
                          <Icon name={IconName.XClose} size="sm" variant="secondary" />
                        </button>
                      </div>
                    ))}

                    {/* Input */}
                    <input
                      type="email"
                      placeholder={emailTags.length === 0 ? "add comma separated emails to invite" : ""}
                      value={email}
                      onChange={handleEmailChange}
                      onKeyDown={handleEmailKeyDown}
                      className="flex-1 min-w-[200px] font-bg-v2-sans font-bg-v2-regular text-bg-v2-md text-text-primary placeholder:text-text-placeholder bg-transparent border-none outline-none"
                    />
                  </div>
                </div>

                {/* Permission Type */}
                <div className="flex flex-col gap-bg-v2-sm w-full">
                  <Text
                    variant="text-sm"
                    weight="medium"
                    color="secondary"
                    className="font-bg-v2-sans"
                  >
                    Permission Type
                  </Text>
                  <div className="flex gap-bg-v2-lg items-start">
                    <div className="flex gap-bg-v2-md items-center">
                      <Radio
                        name="permission"
                        checked={permissionType === 'viewer'}
                        onChange={() => setPermissionType('viewer')}
                        size="md"
                      />
                      <Text
                        variant="text-sm"
                        weight="medium"
                        color="primary"
                        className="font-bg-v2-sans"
                      >
                        Viewer
                      </Text>
                    </div>
                    <div className="flex gap-bg-v2-md items-center">
                      <Radio
                        name="permission"
                        checked={permissionType === 'editor'}
                        onChange={() => setPermissionType('editor')}
                        size="md"
                      />
                      <Text
                        variant="text-sm"
                        weight="medium"
                        color="primary"
                        className="font-bg-v2-sans"
                      >
                        Editor
                      </Text>
                    </div>
                  </div>
                </div>


                {/* Payment Type */}
                {permissionType === 'editor' && <div className="flex flex-col gap-bg-v2-sm w-full">
                  <Text
                    variant="text-sm"
                    weight="medium"
                    color="secondary"
                    className="font-bg-v2-sans"
                  >
                    AI Credit Billing
                  </Text>
                  <div className="flex gap-bg-v2-lg items-start">
                    <div className="flex gap-bg-v2-md items-center">
                      <Radio
                        name="paymentType"
                        checked={paymentType === 'owner_pay'}
                        onChange={() => setPaymentType('owner_pay')}
                        size="md"
                      />
                      <Text
                        variant="text-sm"
                        weight="medium"
                        color="primary"
                        className="font-bg-v2-sans"
                      >
                        Owner
                      </Text>
                    </div>
                    <div className="flex gap-bg-v2-md items-center">
                      <Radio
                        name="paymentType"
                        checked={paymentType === 'collaborator_pay'}
                        onChange={() => setPaymentType('collaborator_pay')}
                        size="md"
                      />
                      <Text
                        variant="text-sm"
                        weight="medium"
                        color="primary"
                        className="font-bg-v2-sans"
                      >
                        Collaborator
                      </Text>
                    </div>
                  </div>
                </div>}
              </div>
            </div>

            {inviteError && <FormAlert variant="error" message={inviteError} />}

            {/* Divider */}
            <div className="bg-border-secondary h-px w-full" />

            {/* Invite Button */}
            <Button
              variant="primary"
              size="small"
              onClick={handleInvite}
              className="w-[155px]"
              disabled={isInviting || (emailTags.length === 0 && !email.trim())}
              label={
                isInviting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Inviting...</span>
                  </div>
                ) : (
                  <>
                    <Icon name={IconName.UsersPlus} size="md" inheritColor />
                    <span>Invite</span>
                  </>
                )
              }
            />
          </div>

          {/* Shareable Link Access Section */}
          <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-xl p-bg-v2-2xl flex flex-col gap-bg-v2-xl w-full">
            <div className="flex items-start justify-between w-full gap-bg-v2-xl">
              <div className="flex flex-col gap-bg-v2-xs flex-1">
                <Text
                  variant="text-md"
                  weight="semibold"
                  color="primary"
                  className="font-bg-v2-sans"
                >
                  Shareable Link Access
                </Text>
                <Text
                  variant="text-sm"
                  weight="regular"
                  color="tertiary"
                  className="font-bg-v2-sans"
                >
                  Any Logged In User with the link can request access to this project
                </Text>
              </div>
              <div className="flex items-center gap-bg-v2-lg shrink-0">
                <Text
                  variant="text-sm"
                  weight="semibold"
                  color={shareableLinkEnabled ? "brand-primary" : "tertiary"}
                  className="font-bg-v2-sans"
                >
                  {shareableLinkEnabled ? 'On' : 'Off'}
                </Text>
                <Switch
                  checked={shareableLinkEnabled}
                  onChange={(e) => handleShareableLinkToggle(e.target.checked)}
                  size="md"
                />
              </div>
            </div>
            
            {shareableLinkEnabled && (
              <>
                <div className="bg-border-secondary h-px w-full" />
                <div className="flex flex-col gap-bg-v2-sm w-full">
                  <div className="relative w-full flex items-center bg-bg-primary-alt border border-border-primary rounded-bg-v2-md shadow-bg-v2-xs">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}${selectedProject?.project_type === 2 ? '/fedramp' : ''}/chat/${selectedProject?.id || ''}`}
                      className="flex-1 bg-transparent px-bg-v2-lg py-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-secondary truncate cursor-default focus:outline-none border-none outline-none"
                      tabIndex={-1}
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`flex items-center justify-center p-2 mr-2 rounded-bg-v2-md transition-all duration-200 shrink-0 ${
                        linkCopied 
                          ? 'bg-bg-brand-primary-alt text-text-brand-primary' 
                          : 'hover:bg-bg-primary-hover text-text-secondary hover:text-text-primary'
                      }`}
                      aria-label="Copy link"
                      type="button"
                    >
                      <Icon 
                        name={linkCopied ? IconName.Check : IconName.Copy} 
                        size="md" 
                        inheritColor 
                      />
                    </button>
                  </div>
                  {linkCopied && (
                    <Text
                      variant="text-xs"
                      weight="medium"
                      color="brand-primary"
                      className="font-bg-v2-sans"
                    >
                      ✓ Link copied to clipboard
                    </Text>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="bg-border-secondary h-px w-full" />

          {/* Members List Section */}
          <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-xl p-bg-v2-2xl flex flex-col gap-bg-v2-xl w-full">
            {/* Tab Navigation */}
            <div ref={tabsRef} className="relative bg-bg-primary border border-border-primary rounded-bg-v2-full shadow-bg-v2-xs w-full p-bg-v2-xs">
              {pillRect && (
                <span
                  aria-hidden="true"
                  className={`absolute rounded-bg-v2-full bg-bg-brand-solid pointer-events-none ${
                    pillAnimate ? 'transition-all duration-300 ease-out motion-reduce:transition-none' : ''
                  }`}
                  style={{ left: pillRect.left, top: pillRect.top, width: pillRect.width, height: pillRect.height }}
                />
              )}
              <div className="flex items-center gap-bg-v2-xs w-full">
                <button
                  ref={registerTab('active')}
                  onClick={() => setActiveTab('active')}
                  className={`relative z-10 flex-1 flex gap-bg-v2-sm items-center justify-center px-bg-v2-xl py-bg-v2-md min-h-[40px] rounded-bg-v2-full transition-colors ${activeTab === 'active'
                    ? 'bg-transparent'
                    : 'bg-transparent hover:bg-bg-secondary'
                    }`}
                >
                  <Text
                    variant="text-sm"
                    weight="semibold"
                    color={activeTab === 'active' ? 'on-brand' : 'secondary'}
                    className="font-bg-v2-sans"
                  >
                    Active Members
                  </Text>
                  <div className="bg-bg-quaternary rounded-bg-v2-full px-bg-v2-sm py-bg-v2-xxs min-w-[22px] shrink-0 flex items-center justify-center">
                    <Text
                      variant="text-xs"
                      weight="medium"
                      color="secondary"
                      className="font-bg-v2-sans"
                    >
                      {activeCount}
                    </Text>
                  </div>
                </button>
                <button
                  ref={registerTab('pending')}
                  onClick={() => setActiveTab('pending')}
                  className={`relative z-10 flex-1 flex gap-bg-v2-sm items-center justify-center px-bg-v2-xl py-bg-v2-md min-h-[40px] rounded-bg-v2-full transition-colors ${activeTab === 'pending'
                    ? 'bg-transparent'
                    : 'bg-transparent hover:bg-bg-secondary'
                    }`}
                >
                  <Text
                    variant="text-sm"
                    weight="semibold"
                    color={activeTab === 'pending' ? 'on-brand' : 'secondary'}
                    className="font-bg-v2-sans"
                  >
                    Pending Invites
                  </Text>
                  <div className="bg-bg-quaternary rounded-bg-v2-full px-bg-v2-sm py-bg-v2-xxs min-w-[22px] shrink-0 flex items-center justify-center">
                    <Text
                      variant="text-xs"
                      weight="medium"
                      color="secondary"
                      className="font-bg-v2-sans"
                    >
                      {pendingCount}
                    </Text>
                  </div>
                </button>
              </div>
            </div>

            {/* Search Input */}
            <div className="bg-bg-primary-alt border border-border-primary rounded-bg-v2-full shadow-bg-v2-xs flex gap-bg-v2-md items-center px-bg-v2-lg py-bg-v2-md w-full">
              <Icon name={IconName.Search} size="sm" variant="secondary" />
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 font-bg-v2-sans font-bg-v2-medium text-bg-v2-xs text-text-placeholder bg-transparent border-none outline-none"
              />
            </div>

            {/* Divider */}
            <div className="bg-border-secondary h-px w-full" />

            {memberActionError && <FormAlert variant="error" message={memberActionError} />}

            {/* Collaborators List */}
            <div className="flex flex-col gap-bg-v2-sm w-full">
              {isFetchingMembers ? (
                <>
                  {[...Array(3)].map((_, index) => (
                    <CollaboratorRowSkeleton key={index} index={index} />
                  ))}
                </>
              ) : filteredMembers.length === 0 ? (
                <div className="p-4 text-center text-text-secondary">No members found</div>
              ) : (
                filteredMembers.map((collaborator, index) => (
                  <React.Fragment key={collaborator.id}>
                    <div
                      className="group flex flex-col items-start px-bg-v2-md py-bg-v2-lg rounded-bg-v2-md w-full hover:bg-bg-primary-hover transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between w-full">
                        {/* Left: Avatar and Info */}
                        <div className="flex gap-bg-v2-lg items-center">
                          {/* Avatar */}
                          <div className="bg-avatar-styles-bg-neutral flex items-center justify-center rounded-full w-[58px] h-[58px] shrink-0">
                            <Text
                              variant="display-xs"
                              weight="medium"
                              color="avatar-neutral"
                              className="font-bg-v2-display"
                            >
                              {collaborator.initials}
                            </Text>
                          </div>

                          {/* Info */}
                          <div className="flex flex-col items-start">
                            <Text
                              variant="text-sm"
                              weight="medium"
                              color="primary"
                              className="font-bg-v2-sans"
                            >
                              {collaborator.name}
                            </Text>
                            <div className="flex flex-col items-start">
                              <Text
                                variant="text-sm"
                                weight="regular"
                                color="quaternary"
                                className="font-bg-v2-sans"
                              >
                                {collaborator.email}
                              </Text>
                              <Text
                                variant="text-xs"
                                weight="regular"
                                color="quaternary"
                                className="font-bg-v2-sans"
                              >
                                Added {collaborator.addedDate}
                              </Text>
                            </div>
                          </div>
                        </div>

                        {/* Right: Role Badge and Actions */}
                        <div className="flex flex-col gap-bg-v2-md items-end justify-center">
                          {/* Role Badge */}
                          <div
                            className={`${collaborator.role === ProjectRole.OWNER
                              ? 'bg-bg-brand-primary-alt border-border-brand-alt'
                              : 'bg-bg-primary border-border-primary'
                              } border px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-sm flex items-center justify-center`}
                          >
                            <Text
                              variant="text-xs"
                              weight="medium"
                              color="secondary"
                              className="font-bg-v2-sans"
                            >
                              {collaborator.role}
                            </Text>
                          </div>

                          {/* More Actions (only for non-owners) */}
                          {collaborator.role !== ProjectRole.OWNER && (
                            <button
                              onClick={(e) => handleOpenMenu(e, collaborator)}
                              className={`p-0 transition-opacity hover:opacity-70 ${activeMenuId === collaborator.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                            >
                              <Icon name={IconName.DotsHorizontal} size="md" variant="secondary" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {index < filteredMembers.length - 1 && (
                      <div className="bg-border-secondary h-px w-full" />
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* More Actions Dropdown */}
      <MoreActions
        items={menuItems}
        isOpen={!!activeMenuId && !!anchorEl}
        onClose={() => {
          setActiveMenuId(null);
          setAnchorEl(null);
        }}
        triggerRef={triggerRef}
        position="bottom-right"
        width={190}
      />
    </div>
  );
};
